/**
 * Situation Board — real-time intelligence overview panel.
 *
 * Subscribes to `analysis:complete` events from the analysis engine
 * and renders collapsible sections for actionable intelligence:
 *   - BREAKING — from breaking-news detection
 *   - FOCAL POINTS — top 5 from focal-point analysis
 *   - ACTIVE SURGES — from surge detection
 */

import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { relativeTime } from '@/utils/time';
import { getAnalysisResult } from '@/storage/indexeddb';
import type { AnalysisResult, FocalPoint, SurgeEvent } from '@/intelligence/analysis/types';
import type { BreakingNewsAlert } from '@/intelligence/analysis/breaking-news';
import type { EventType } from '@/sources/base-source';

// Domain tags that can appear on focal points, mapped to CSS class names
const DOMAIN_TAG_MAP: Record<string, string> = {
  conflict: 'conflict',
  military: 'military',
  maritime: 'maritime',
  cyber: 'cyber',
  nuclear: 'nuclear',
  infrastructure: 'infrastructure',
  diplomatic: 'diplomatic',
  economic: 'economic',
  political: 'political',
  social: 'social',
  environmental: 'environmental',
  health: 'health',
  aviation: 'aviation',
  fire: 'environmental',
  earthquake: 'environmental',
  weather: 'environmental',
  protest: 'social',
  sanction: 'diplomatic',
  cyber_incident: 'cyber',
  vessel_position: 'maritime',
  flight_position: 'aviation',
  disaster: 'environmental',
  custom: 'political',
};

function getDomainTagClass(tag: string): string {
  return DOMAIN_TAG_MAP[tag.toLowerCase()] ?? 'political';
}

export class SituationBoard {
  private container: HTMLElement;
  private cleanupFns: (() => void)[] = [];
  private lastResult: AnalysisResult | null = null;
  private cachedResultAge: number | null = null; // ms since cached result was saved
  private isLive = false; // true once a live analysis:complete arrives
  private collapsedSections = new Set<string>();
  private badgeEl: HTMLElement | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'situation-board';
    this.container.style.cssText = 'height: 100%;';

    // Try to get the last analysis result from the engine (if already running)
    const engine = getAnalysisEngine();
    if (engine) {
      this.lastResult = engine.getLastResult();
      if (this.lastResult) this.isLive = true;
    }

    // Subscribe to analysis results
    this.cleanupFns.push(
      eventBus.on('analysis:complete', (result) => {
        this.lastResult = result;
        this.isLive = true;
        this.cachedResultAge = null;
        this.render();
      }),
    );

    // Initial render (may be empty — warm start loads async below)
    this.render();

    // Warm start: try to restore cached analysis result from IndexedDB
    if (!this.lastResult) {
      this.loadCachedResult();
    }
  }

  /** Attempt to load a cached AnalysisResult from IndexedDB for instant display. */
  private async loadCachedResult(): Promise<void> {
    try {
      const cached = await getAnalysisResult();
      if (cached && !this.isLive) {
        this.lastResult = cached.result as AnalysisResult;
        this.cachedResultAge = Date.now() - cached.savedAt;
        this.render();
      }
    } catch {
      // Silently ignore — will show warming up message
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }

  /** Set the external badge element for alert count updates. */
  setBadgeElement(el: HTMLElement): void {
    this.badgeEl = el;
    this.updateBadge();
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
    this.lastResult = null;
  }

  /** Count active alerts for the badge. */
  private getAlertCount(): number {
    if (!this.lastResult) return 0;
    const r = this.lastResult;
    return (
      r.breakingNews.length +
      r.focalPoints.filter(fp => fp.urgencyLevel === 'critical').length +
      r.surges.length
    );
  }

  private updateBadge(): void {
    if (!this.badgeEl) return;
    const count = this.getAlertCount();
    this.badgeEl.textContent = count > 0 ? String(count) : '';
    this.badgeEl.style.display = count > 0 ? '' : 'none';
  }

  private render(): void {
    this.container.textContent = '';
    this.updateBadge();

    if (!this.lastResult) {
      const empty = document.createElement('div');
      empty.className = 'sit-empty';
      empty.textContent = 'Analysis engine warming up\u2026';
      this.container.appendChild(empty);
      return;
    }

    // Show cached data indicator when displaying restored result
    if (this.cachedResultAge !== null && !this.isLive) {
      const banner = document.createElement('div');
      banner.className = 'sit-cached-banner';
      const mins = Math.round(this.cachedResultAge / 60_000);
      const label = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
      banner.textContent = `Last updated: ${label} \u00b7 Refreshing\u2026`;
      this.container.appendChild(banner);
    }

    const r = this.lastResult;

    // BREAKING
    if (r.breakingNews.length > 0) {
      this.container.appendChild(
        this.buildSection('breaking', 'Breaking', null, () =>
          this.renderBreaking(r.breakingNews),
        ),
      );
    }

    // FOCAL POINTS
    this.container.appendChild(
      this.buildSection('focal', 'Focal Points', `Top ${Math.min(5, r.focalPoints.length)}`, () =>
        this.renderFocalPoints(r.focalPoints.slice(0, 5)),
      ),
    );

    // ACTIVE SURGES
    if (r.surges.length > 0) {
      this.container.appendChild(
        this.buildSection('surges', 'Active Surges', String(r.surges.length), () =>
          this.renderSurges(r.surges),
        ),
      );
    }

  }

  /** Build a collapsible section wrapper. */
  private buildSection(
    id: string,
    label: string,
    countText: string | null,
    renderBody: () => HTMLElement,
  ): HTMLElement {
    const section = document.createElement('div');
    section.className = 'sit-section';
    if (this.collapsedSections.has(id)) {
      section.classList.add('collapsed');
    }

    const header = document.createElement('div');
    header.className = 'sit-header';
    header.onclick = () => {
      section.classList.toggle('collapsed');
      if (section.classList.contains('collapsed')) {
        this.collapsedSections.add(id);
      } else {
        this.collapsedSections.delete(id);
      }
    };

    const labelEl = document.createElement('span');
    labelEl.className = 'sit-label';
    labelEl.textContent = label;
    header.appendChild(labelEl);

    if (countText) {
      const count = document.createElement('span');
      count.className = 'sit-count';
      count.textContent = countText;
      header.appendChild(count);
    }

    const chevron = document.createElement('span');
    chevron.className = 'sit-chevron';
    chevron.textContent = '\u25BE';
    header.appendChild(chevron);

    section.appendChild(header);

    const body = document.createElement('div');
    body.className = 'sit-body';
    body.appendChild(renderBody());
    section.appendChild(body);

    return section;
  }

  // --- Section renderers ---

  private renderBreaking(alerts: BreakingNewsAlert[]): HTMLElement {
    const frag = document.createElement('div');
    // Filter out pipeline artifacts (JSON titles from corrupted cache)
    const clean = alerts.filter(a => {
      const t = a.title?.trim();
      return t && !t.startsWith('{') && !t.startsWith('[');
    });
    for (const alert of clean) {
      const card = document.createElement('div');
      card.className = 'breaking-card';

      const label = document.createElement('div');
      label.className = 'breaking-label';
      label.textContent = '\u25B2 BREAKING';
      card.appendChild(label);

      const title = document.createElement('div');
      title.className = 'breaking-title';
      title.textContent = alert.title;
      card.appendChild(title);

      const meta = document.createElement('div');
      meta.className = 'breaking-meta';
      meta.textContent = `${alert.source} \u00B7 ${relativeTime(alert.timestamp)}`;
      card.appendChild(meta);

      card.onclick = () => {
        // Emit event selection to open detail panel
        eventBus.emit('event:selected', {
          id: alert.eventId,
          source: alert.source,
          timestamp: alert.timestamp,
          type: 'conflict',
          title: alert.title,
          severity: alert.severity,
        });
      };

      frag.appendChild(card);
    }
    return frag;
  }

  private renderFocalPoints(points: FocalPoint[]): HTMLElement {
    const frag = document.createElement('div');

    if (points.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'sit-empty';
      empty.style.padding = '8px';
      empty.textContent = 'No focal points detected yet';
      frag.appendChild(empty);
      return frag;
    }

    for (const fp of points) {
      const card = document.createElement('div');
      card.className = 'focal-card';

      // Score badge
      const score = document.createElement('div');
      score.className = 'focal-score';
      if (fp.urgencyLevel === 'critical') score.classList.add('crit');
      else if (fp.urgencyLevel === 'elevated') score.classList.add('elev');
      else score.classList.add('watch');
      score.textContent = String(fp.score);
      card.appendChild(score);

      // Body
      const body = document.createElement('div');
      body.className = 'focal-body';

      const entityEl = document.createElement('div');
      entityEl.className = 'focal-entity';
      entityEl.textContent = fp.entity;
      body.appendChild(entityEl);

      // Lead signal — the most important headline driving this focal point
      const leadEl = document.createElement('div');
      leadEl.className = 'focal-lead-signal';
      leadEl.textContent = fp.signals?.[0]?.description ?? '';
      leadEl.style.cssText = 'color: #e2e8f0; font-size: 13px; margin: 4px 0 6px; line-height: 1.4;';
      body.appendChild(leadEl);

      // Signal breakdown — what types of events are contributing
      const breakdownEl = document.createElement('div');
      breakdownEl.className = 'focal-breakdown';
      breakdownEl.style.cssText = 'color: #94a3b8; font-size: 11px; margin-bottom: 6px;';
      const typeCounts: Record<string, number> = {};
      for (const s of fp.signals ?? []) {
        const t = s.type.replace(/_/g, ' ');
        typeCounts[t] = (typeCounts[t] ?? 0) + 1;
      }
      breakdownEl.textContent = Object.entries(typeCounts)
        .map(([t, c]) => `${c} ${t}`)
        .join(' \u00b7 ');
      body.appendChild(breakdownEl);

      // Domain tags from signals (exclude meaningless types)
      const tags = document.createElement('div');
      tags.className = 'focal-tags';
      const seenTags = new Set<string>();
      const meaningfulSignals = (fp.signals ?? []).filter(s =>
        s.type !== 'custom' && s.type !== 'news'
      );
      for (const signal of meaningfulSignals) {
        const tagName = signal.type;
        if (seenTags.has(tagName)) continue;
        seenTags.add(tagName);
        if (seenTags.size > 3) break;
        const tag = document.createElement('span');
        tag.className = `focal-tag ${getDomainTagClass(tagName)}`;
        tag.textContent = tagName.replace(/_/g, ' ');
        tags.appendChild(tag);
      }
      body.appendChild(tags);

      // INVESTIGATE link (WP-S13.4)
      const invLink = document.createElement('span');
      invLink.className = 'focal-investigate';
      invLink.style.cssText = `
        font-size: 8px; color: #00b4d8; cursor: pointer;
        margin-top: 2px; display: inline-block;
        letter-spacing: 0.5px; font-weight: 600;
      `;
      invLink.textContent = 'INVESTIGATE';
      invLink.addEventListener('click', (e) => {
        e.stopPropagation();
        eventBus.emit('investigation:request', { query: fp.entity, tier: 'STANDARD' });
      });
      body.appendChild(invLink);

      // Click focal card to open focal point detail view
      card.onclick = () => {
        eventBus.emit('focal-point:selected', fp);
      };

      card.appendChild(body);
      frag.appendChild(card);
    }
    return frag;
  }

  private renderSurges(surges: SurgeEvent[]): HTMLElement {
    const frag = document.createElement('div');
    for (const surge of surges) {
      const row = document.createElement('div');
      row.className = 'surge-row';

      const arrow = document.createElement('div');
      arrow.className = 'surge-arrow';
      arrow.textContent = '\u25B2';
      row.appendChild(arrow);

      const info = document.createElement('div');
      info.className = 'surge-info';

      const type = document.createElement('div');
      type.className = 'surge-type';
      type.textContent = formatEventType(surge.sourceType);
      info.appendChild(type);

      const region = document.createElement('div');
      region.className = 'surge-region';
      region.textContent = surge.region;
      info.appendChild(region);

      row.appendChild(info);

      const val = document.createElement('div');
      val.className = 'surge-val';
      val.textContent = `${surge.ratio}x`;
      row.appendChild(val);

      // Click surge row to emit event for detail panel
      row.onclick = () => {
        eventBus.emit('event:selected', {
          id: `surge-${surge.sourceType}`,
          source: surge.sourceType,
          timestamp: new Date().toISOString(),
          type: surge.sourceType as EventType,
          title: `${formatEventType(surge.sourceType)} surge: ${surge.ratio}x baseline`,
          description: `${surge.count} events in the last hour vs ${surge.baseline} avg. Region: ${surge.region}`,
          severity: surge.ratio >= 5 ? 'critical' : 'high',
        });
      };

      frag.appendChild(row);
    }
    return frag;
  }

}

/** Format event type slug into display label. */
function formatEventType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Factory function for the situation board panel. */
export function createSituationBoard(): { element: HTMLElement; board: SituationBoard } {
  const board = new SituationBoard();
  const el = board.getElement();
  (el as HTMLElement & { destroy?: () => void }).destroy = () => board.destroy();
  return { element: el, board };
}
